import { Button, Col, Form, Input, message, Modal, Row, Spin, Typography } from "antd";
import { useState } from "react";
import { generateFormRules } from "../../../../helpers/formRules";
import { getErrorMessage } from "../../../../helpers/errorHandler";
import { EArticleCategoriesStatus, MasterArticleCategoryProperties } from "../../../../types/blog.type";
import { httpRequest } from "../../../../helpers/api";
import { BaseResponseProps } from "../../../../types/config.type";
import { successModal } from "../../../../helpers/modal";
import { CloseOutlined } from "@ant-design/icons";

interface CreateCategoryProps {
	open?: boolean;
	onClose?: () => void;
	onSuccess?: () => void;
}
interface FormValues {
	categoryName: string;
	subCategories: {
		subCategoryName: string;
	}[];
}
const CreateCategoryModal: React.FC<CreateCategoryProps> = ({ open, onClose, onSuccess }) => {
	const [form] = Form.useForm<FormValues>();
	const [isActionLoading, setIsActionLoading] = useState<boolean>(false);

	const doSubmit = async (values: FormValues) => {
		setIsActionLoading(true);
		try {
			const dto = {
				parentCategoryId: undefined,
				categoryName: values.categoryName,
				status: EArticleCategoriesStatus.PUBLISH,
			};
			const resCategory = await httpRequest.post<BaseResponseProps<MasterArticleCategoryProperties>>(
				"master-article-categories",
				dto
			);

			for (const sub of values.subCategories ?? []) {
				await httpRequest.post("master-article-categories", {
					parentCategoryId: resCategory.data.payload.categoryId,
					categoryName: sub.subCategoryName,
					status: EArticleCategoriesStatus.PUBLISH,
				});
			}

			successModal({
				title: "Created!",
				subtitle: "Category successfully created!",
			});

			form.resetFields();
			onSuccess?.();
			onClose?.();
		} catch (error) {
			message.error(getErrorMessage(error));
		}
		setIsActionLoading(false);
	};

	return (
		<Modal
			destroyOnClose={true}
			title={`Create New Category`}
			open={open}
			onCancel={() => {
				onClose?.();
			}}
			onOk={form.submit}
			okText={"Add"}
			okButtonProps={{
				loading: isActionLoading,
			}}
			width={520}
		>
			<Spin spinning={isActionLoading}>
				<Form
					form={form}
					preserve={false}
					layout="vertical"
					name="basic"
					autoComplete="off"
					initialValues={{ isPublished: true }}
					onFinish={doSubmit}
				>
					<Form.Item
						name="categoryName"
						label="New Category Name"
						rules={generateFormRules("Category Name", ["required"])}
					>
						<Input placeholder="Enter a new category name" />
					</Form.Item>

					<Form.List name="subCategories">
						{(fields, { add, remove }) => (
							<div style={{ display: "flex", rowGap: 16, flexDirection: "column" }}>
								{fields.length > 0 ? (
									<Typography.Title
										style={{
											margin: 0,
										}}
										level={4}
									>
										Sub Category
									</Typography.Title>
								) : null}

								{fields.map((field) => (
									<Row
										gutter={[12, 12]}
										style={{
											alignItems: "flex-end",
										}}
									>
										<Col flex="auto">
											<Form.Item
												style={{
													marginBottom: 0,
												}}
												key={field.key}
												label={`Sub Category Name ${field.name + 1}`}
												name={[field.name, "subCategoryName"]}
											>
												<Input placeholder="Enter a new sub category name" />
											</Form.Item>
										</Col>
										<Col>
											<Button
												danger
												type="primary"
												onClick={() => remove(field.name)}
											>
												<CloseOutlined />
											</Button>
										</Col>
									</Row>
								))}

								<Button
									type="primary"
									onClick={() => add()}
									block
								>
									+ Add Sub Category
								</Button>
							</div>
						)}
					</Form.List>
				</Form>
			</Spin>
		</Modal>
	);
};
export default CreateCategoryModal;
